import React, { useState } from 'react';
import { ICON } from '../icons/iconIds';
import SelkirkIcon from './icons/SelkirkIcon';
import IconText from './icons/IconText';
import { qgisService } from '../services/qgisService';
import { authService } from '../auth/authService';
import Button from './ui/Button';
import Card from './ui/Card';
import './HelloWorld.css';

const HelloWorld = ({ user }) => {
  const [healthStatus, setHealthStatus] = useState('');
  const [healthTone, setHealthTone] = useState('');
  const [testing, setTesting] = useState(false);

  const testHealthEndpoint = async () => {
    setTesting(true);
    setHealthStatus('Testing...');
    
    try {
      const result = await qgisService.healthCheck();

      if (result.status === 'healthy') {
        setHealthTone('success');
        setHealthStatus(`API Health Check Passed\n${result.message}`);
      } else {
        console.error('❌ QGIS Service unhealthy:', result.message);
        setHealthTone('warning');
        setHealthStatus(`API Health Check Warning\n${result.message}`);
      }
    } catch (error) {
      console.error('❌ Health test exception:', error);
      setHealthTone('error');
        setHealthStatus(`Connection Test Failed: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const testQGISCapabilities = async () => {
    setTesting(true);
    setHealthStatus('Testing QGIS Capabilities...');
    
    try {
      const token = await authService.getAccessToken();
      
      const testResponse = await fetch(`https://minto-qgis-apim.azure-api.net/qgis/qgis_mapserv.fcgi?SERVICE=WMS&REQUEST=GetCapabilities&VERSION=1.3.0&MAP=${qgisService.qgisProject.path}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/xml'
        }
      });
      
      const testText = await testResponse.text();
      
      if (testText.trim().toLowerCase().startsWith('<!doctype html') || 
          testText.trim().toLowerCase().startsWith('<html')) {
        console.error('❌ Server returned HTML instead of XML');
        setHealthTone('error');
        setHealthStatus(`Authentication Issue: Server returned HTML error page instead of XML.\n\nResponse preview:\n${testText.substring(0, 200)}...`);
        return;
      }
      
      const wmsCapabilities = await qgisService.getWMSCapabilities();
      const wfsCapabilities = await qgisService.getWFSCapabilities();
      
      const totalLayers = wmsCapabilities.layers.length + wfsCapabilities.featureTypes.length;
      setHealthTone('success');
      setHealthStatus(`QGIS Server Connected!\nWMS Layers: ${wmsCapabilities.layers.length}\nWFS Layers: ${wfsCapabilities.featureTypes.length}\nTotal: ${totalLayers} layers available\n\nLayers found:\n${wmsCapabilities.layers.map(l => `• ${l.name}`).join('\n')}`);
      
    } catch (error) {
      console.error('❌ QGIS capabilities test failed:', error);
      setHealthTone('error');
      setHealthStatus(`QGIS Test Failed: ${error.message}\n\nCheck console for detailed error information.`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Welcome Header */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1 className="dashboard-title">
            Welcome back, {user?.name?.split(' ')[0] || 'User'}! <SelkirkIcon name={ICON.uiWaveHello} size={24} decorative />
          </h1>
          <p className="dashboard-subtitle">
            You're connected to Selkirk Copper's unified data platform
          </p>
        </div>
        
        <div className="user-badge">
          <div className="user-avatar">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="user-details">
            <span className="user-name">{user?.name || 'User'}</span>
            <span className="user-role">Platform User</span>
          </div>
        </div>
      </div>


      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* API Connection Test */}
        <Card variant="elevated" className="api-test-card">
          <div className="card-header">
            <IconText icon={ICON.uiApiLink} as="h3">API Connection Test</IconText>
            <div className="connection-status online">
              <span className="status-dot"></span>
              Connected
            </div>
          </div>
          
          <div className="api-test-content">
            <p>Test your connection to the QGIS backend through Azure API Management:</p>
            
            <div className="test-buttons">
              <Button
                variant="primary"
                loading={testing}
                onClick={testHealthEndpoint}
                className="test-button"
              >
                {testing ? 'Testing...' : 'Test API Health'}
              </Button>
              
              <Button
                variant="secondary"
                loading={testing}
                onClick={testQGISCapabilities}
                className="test-button"
              >
                {testing ? 'Testing...' : 'Test QGIS Layers'}
              </Button>
            </div>
            
            {healthStatus && (
              <div className={`health-result ${healthTone === 'success' ? 'success' : healthTone === 'warning' ? 'warning' : 'error'}`}>
                <p>{healthStatus}</p>
              </div>
            )}
          </div>
          
          <div className="api-details">
            <div className="detail-item">
              <span className="detail-label">Endpoint:</span>
              <span className="detail-value">APIM Gateway</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Authentication:</span>
              <span className="detail-value">Azure AD Bearer Token</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Your Account:</span>
              <span className="detail-value">{user?.email || user?.username}</span>
            </div>
          </div>
        </Card>

      </div>

      {/* System Information */}
      <div className="system-info">
        <Card variant="outline" className="info-card">
          <div className="info-content">
            <IconText icon={ICON.uiTools} as="h3">System Information</IconText>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Platform Version</span>
                <span className="info-value">Wolfwalker v1.0.0</span>
              </div>
              <div className="info-item">
                <span className="info-label">Environment</span>
                <span className="info-value">Production</span>
              </div>
              <div className="info-item">
                <span className="info-label">Authentication</span>
                <span className="info-value">Azure AD</span>
              </div>
              <div className="info-item">
                <span className="info-label">Data Source</span>
                <span className="info-value">QGIS Server + APIM</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HelloWorld;

